import React, {useState, useEffect} from 'react';
import { useToasts } from '../../Components/Toasts';
import Button from '../../Components/Button/Button';
import ButtonRow from '../../Components/Button/ButtonRow';
import { useAsk } from '../../Data/Help';
import Text from '../../Components/Formatting/Text';
import { b64SafeDecode, b64SafeEncode, classNames, loadStylesheet, useRedirect } from '../../Services/helpers';
import Doc from './Doc';
import Feedback from './Feedback';
import { useHelp } from '../../Components/Help';
import TextArea from '../../Components/Inputs/TextArea';
import { useSocketListener, useSocketRecipient } from '../../Components/Socket';
import Markdown from '../../Components/Formatting/Markdown';

const recognitionAvailable = !!(window.webkitSpeechRecognition || window.SpeechRecognition);
let recognition;
if (recognitionAvailable) {
    recognition = window.webkitSpeechRecognition ?
        new window.webkitSpeechRecognition() :
        new window.SpeechRecognition();
    recognition.lang = "en-GB";
    recognition.interimResults = true;
}

const socketTopic = 'help-widget';

const Help = ({prefill}) => {

    const [streamingAnswer, setStreamingAnswer] = useState('');
    useSocketListener({topic: socketTopic}, (chunk) => {
        setStreamingAnswer(a => `${a}${chunk.chunk}`);
    });
    const socketRecipient = useSocketRecipient(socketTopic);

    const [dictating, setDictating] = useState(false);
    const [tmpQuestion, setTmpQuestion] = useState(prefill ? b64SafeDecode(prefill) : '');
    const [question, setQuestion] = useState(prefill ? b64SafeDecode(prefill) : '');
    const {answer, activityId, docs, isLoading, refresh} = useAsk(false, question, socketRecipient);
    const {showError} = useToasts();
    const {go, Redirect} = useRedirect();

    const [theAnswer, setTheAnswer] = useState(null);
    useEffect(() => {
        setTheAnswer(answer);
    }, [answer]);

    useEffect(() => {
        if (!recognitionAvailable) {
            return;
        }

        const onResult = (event) => setTmpQuestion(event.results[0][0].transcript);
        const onSpeechEnd = () => stopDictating();

        const onError = (event) => {
            console.error(event);
            setDictating(false);
            recognition.stop();
            showError(`Speech recognition error detected: ${event.error}`)
        }

        recognition.addEventListener('result', onResult);
        recognition.addEventListener('speechend', onSpeechEnd);
        recognition.addEventListener('error', onError);

        return () => {
            recognition.removeEventListener('result', onResult);
            recognition.removeEventListener('speechend', onSpeechEnd);
            recognition.removeEventListener('error', onError);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const toggleDictation = () => dictating ? stopDictating() : startDictating();

    const startDictating = () => {
        setDictating(true);
        recognition.start();
    }

    const stopDictating = () => {
        recognition.stop();
        setDictating(false);
        setTimeout(() => {
            handleSubmit();
        }, 500);
    }

    const handleSubmit = () => {
        if (!tmpQuestion) {
            return;
        }

        setStreamingAnswer('');
        setTheAnswer(null);
        setQuestion(tmpQuestion);
        refresh();
        go('/more/help/'+b64SafeEncode(tmpQuestion));
    };

    return <>
        <Question {...{handleSubmit, tmpQuestion, setTmpQuestion, question, isLoading, dictating, recognitionAvailable, toggleDictation}} />

        <Answer {...{streamingAnswer, answer: theAnswer, activityId, docs}} />

        <Redirect />
    </>
}

const Question = ({handleSubmit, tmpQuestion, setTmpQuestion, question, isLoading, dictating, recognitionAvailable, toggleDictation}) => {
    const {openLiveChat, unreadMessages} = useHelp();

    return <TextArea
        prompt={"Ask any questions about getSoapy..."}
        value={tmpQuestion}
        onChange={setTmpQuestion}
        onSubmit={handleSubmit}
        disabled={(question && isLoading) || dictating}
        leftButtons={<Button
            theme="light"
            icon="User"
            label="Humans"
            onClick={() => openLiveChat()}
            badge={unreadMessages}
        />}
        rightButtons={<ButtonRow>
            <Button
                className={classNames(dictating ? 'animate-pulse' : null, 'hidden')}
                theme={dictating ? 'danger' : 'light'}
                icon="Microphone"
                label="Speak"
                iconTheme={dictating ? 'solid' : 'light'}
                hide={!recognitionAvailable}
                onClick={toggleDictation}
            />
            <Button
                type="submit"
                theme="primary"
                label="Ask"
                disabled={dictating || (question && isLoading)}
                loading={(question && isLoading)}
            />
        </ButtonRow>}
    />
}

const Answer = ({streamingAnswer, answer, activityId, docs}) => {

    const [openDoc, setOpenDoc] = useState(null);
    const [docTitle, setDocTitle] = useState(null);

    loadStylesheet('fonts');

    // console.log('answer', typeof answer, answer);
    // console.log('streamingAnswer', typeof streamingAnswer, streamingAnswer);

    if (streamingAnswer && !answer) {
        return <div className="mt-4">
            <Markdown markdown={streamingAnswer}
                className="mt-2 text-left prose prose-base prose-slate inline-doc"
            />
        </div>
    }

    if (!answer) {
        return null;
    }

    return <>
        <div className="mt-4">
            <div className="mt-2 text-left prose prose-base prose-slate inline-doc">
                <Text rich={answer} />
            </div>

            <Feedback activityId={activityId} container />

            <h3 className="mt-4 text-lg leading-6 font-bold text-gray-900">Related articles</h3>
            <ul className="mt-2 border-t border-b border-gray-200 divide-y divide-gray-200">
                {docs.map(doc => <li key={doc.id}
                    className="py-3 flex justify-between items-center link"
                    onClick={() => {
                        setOpenDoc(doc.id);
                        setDocTitle(doc.title);
                    }}
                >
                    {doc.title}
                </li>)}
            </ul>
        </div>

        <Doc docId={openDoc}
            onClose={() => {
                setOpenDoc(null);
                setDocTitle(null);
            }}
            title={docTitle}
        />
    </>
}

export default Help;
